
import { User } from "@/models/user";
import { Agent } from "@/models/agent";
import { toast } from "@/components/ui/use-toast";
import { logger } from "@/utils/logger";
import { useNavigate } from "react-router-dom";
import { PurchaseService } from "@/services/purchase-service";

interface PurchasePaidAgentProps {
  agent: Agent;
  user: User | null;
  walletBalance: number | null;
  setPurchasing: (value: boolean) => void;
  setIsCheckingStatus: (value: boolean) => void;
  setError: (value: string | null) => void;
  setPurchaseSuccess: (value: boolean) => void;
  setWalletBalance: (value: number) => void;
  navigate: ReturnType<typeof useNavigate>;
}

/**
 * Handles the paid agent purchase process
 */
export const handlePurchasePaidAgent = async ({
  agent,
  user,
  walletBalance,
  setPurchasing,
  setIsCheckingStatus,
  setError,
  setPurchaseSuccess,
  setWalletBalance,
  navigate
}: PurchasePaidAgentProps) => {
  if (!agent) return;
  
  // Use agent price or default to 10 if not specified
  const price = agent.price || 10; 
  
  setPurchasing(true);
  setError(null);
  
  try {
    if (walletBalance !== null && price > walletBalance) {
      logger.warn("Insufficient funds", { 
        userId: user?.id, 
        agentId: agent.id, 
        required: price, 
        available: walletBalance 
      });
      toast({
        title: "Insufficient funds",
        description: "Please add funds to your wallet before making this purchase",
        variant: "destructive"
      });
      setPurchasing(false);
      return;
    }
    
    // Check if user already purchased this agent
    setIsCheckingStatus(true);
    try {
      if (user) {
        const purchases = await PurchaseService.getPurchasedAgents(user.id);
        const alreadyPurchased = purchases.some(p => p.agent_id === agent.id);
        
        if (alreadyPurchased) {
          logger.info("Agent already purchased", { userId: user.id, agentId: agent.id });
          setPurchaseSuccess(true);
          toast({
            title: "Already purchased",
            description: "You already own this agent",
            variant: "default"
          });
          setPurchasing(false);
          return;
        }
      }
    } catch (err) {
      logger.error("Error checking purchase status", { error: err, userId: user?.id, agentId: agent.id });
      // Continue with purchase attempt even if check fails
    } finally {
      setIsCheckingStatus(false);
    }
    
    // For now, we'll simulate a successful purchase since database writes are restricted
    logger.info("Processing purchase", { userId: user?.id, agentId: agent.id, price });
    setTimeout(() => {
      setPurchaseSuccess(true);
      toast({
        title: "Purchase successful",
        description: `You've successfully purchased ${agent.name}`,
        variant: "default"
      });
      
      // Update wallet balance locally
      if (walletBalance !== null) {
        setWalletBalance(walletBalance - price);
      }
      
      setPurchasing(false);
      
      // Redirect to agent runtime
      setTimeout(() => {
        navigate(`/runtime/${agent.id}`);
      }, 1000);
    }, 1500);
    
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : "Purchase failed";
    logger.error("Purchase error", { error: errorMessage, agentId: agent.id, userId: user?.id });
    setError(errorMessage);
    toast({
      title: "Purchase failed",
      description: "There was an error processing your purchase. Please try again later.",
      variant: "destructive"
    });
    setPurchasing(false);
  }
};
